import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import mixpanel from 'mixpanel-browser';
import { Routes, Route } from 'react-router-dom';

import { Landing } from './pages/Landing/index.js';
import { AboutUs } from './pages/AboutUs/index.jsx';

import { Partner } from './pages/Partner';
import { FreeTrialReport } from './pages/Partner/FreeTrialReport';
import { Dashboard } from './components/Dashboard.js';
import { LayoutWrapper } from './components/LayoutWrapper.js';

import { AutoLoans } from './components/Loans/Auto/index.js';
import { MortgageLoans } from './components/Loans/Mortgage/index.js';

import { ProfilePage } from './components/ProfilePage/index.js';
// import { ComparisonSummary } from './components/RatesSummary/ComparisonSummary/index.js';
import { RatesComparisonSummary } from './components/RatesComparisonSummary.js';
// import { RatesAnalytics } from './components/RatesSummary/RatesAnalytics/index.js';
import { RatesComparisonAnalytics } from './components/RatesComparisionAnalytics.js';

// Form components
import { Login } from './components/Forms/Login.js';
import { SignUp } from './components/Forms/SignUp.js';
import { ForgetPassword } from './components/Forms/ForgetPassword.js';
import { VerifyPassword } from './components/Forms/VerifyPassword.js';
import { ResetPassword } from './components/Forms/ResetPassword.js';
import { Contact } from './components/Forms/Contact/Contact.js';
import { PrivacyPolicy } from './components/Footer/PrivacyPolicy.js';
import { TermsAndCondition } from './components/Footer/TermsAndCondition.js';
import { TermsOfService } from './components/Footer/TermsOfService.js';
import { FAQ } from './components/Footer/FAQ.js';

// import { Survey } from './components/Footer/Survey/Survey.js';
import { Surveys } from './components/Surveys/index.js';
import { SurveyAdminView } from './components/Surveys/SurveyAdminView.js';
import { Experience } from './components/Footer/Survey/Experience.js';
import { FinanceAuto } from './components/Footer/Survey/FinanceAuto.js';
import { FinanceMortgage } from './components/Footer/Survey/FinanceMortgage.js';
import { InsuranceHome } from './components/Footer/Survey/HomeOwnerInsurance.js';
import { InsuranceAuto } from './components/Footer/Survey/AutoInsurance.js';
import { ThankYou } from './components/Footer/Survey/ThankYou.js';
import { Blog } from './components/Footer/Blog.js';
import { BlogPost } from './components/Footer/BlogPost.js';
import { CareersPage } from './pages/Career/index.js';

import { useLocalStorage } from './CustomHooks/useLocalStorage.js';

import { setUserInfo, getUserByEmail } from './actions/usersActions.js';

import { TOKEN, handleSettingsClick, LOGOUT } from './constants/index.js';

import { MIXPANEL_DEVICE_USER_LINK } from './MixpanelDeviceUserLink/mixpanel_device_user_link.js';

// import { NotFound } from './components/404.js';

import './App.css';

const App = () => {
  const dispatch = useDispatch();

  const { user, jwt } = useSelector(({ userReducer: { user, jwt } }) => ({
    user,
    jwt,
  }));

  const [value] = useLocalStorage(TOKEN);

  useEffect(() => {
    if (jwt || value) {
      if (!localStorage.getItem(TOKEN)) {
        return;
      }
      const user = atob(localStorage.getItem(TOKEN)?.split('.')?.[1]);
      const parsedUser = JSON.parse(user);

      if (parsedUser && !parsedUser?.id && parsedUser?.email) {
        // get by email
        dispatch(getUserByEmail({ email: parsedUser?.email }));
        mixpanel.track('Auth Token User Email', {
          email: parsedUser.email,
        });
        return;
      } else if (parsedUser && !parsedUser.id && !parsedUser.email) {
        // Let's track this
        const date = new Date(parsedUser.exp * 1000);
        const now = new Date();
        if (date < now) {
          handleSettingsClick[LOGOUT]?.(dispatch, parsedUser);
          return;
        }
      } else if (parsedUser) {
        const date = new Date(parsedUser.exp * 1000);
        const now = new Date();
        if (date < now) {
          handleSettingsClick[LOGOUT]?.(dispatch, parsedUser);
          return;
        }

        dispatch((dispatch) => setUserInfo(dispatch, parsedUser));
      }
    }
  }, [jwt, value]);

  useEffect(() => {
    const anonymousDeviceId = mixpanel.get_distinct_id();
    const TIFFANY_ID = '79f271c4-d722-43ac-bafd-5fcabba490d2';
    const DAPHNE_ID = '6770b1ae-3017-4930-af70-b6a7e27a046f';
    if (
      (user?.id &&
        MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][
          anonymousDeviceId
        ] === user?.id) ||
      // This is for Facebook Auth Sign up Tiffany
      MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][
        anonymousDeviceId
      ] === TIFFANY_ID ||
      // this one is for Facebook Auth Sign Up Daphne  6770b1ae-3017-4930-af70-b6a7e27a046f
      MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][
        anonymousDeviceId
      ] === DAPHNE_ID
    ) {
      const isUserAliasedAlready = localStorage.getItem(user.id || TIFFANY_ID || DAPHNE_ID);
      if (isUserAliasedAlready) {
        return;
      }

      if (
        MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][
          anonymousDeviceId
        ] === TIFFANY_ID
      ) {
        mixpanel.alias(TIFFANY_ID);
        mixpanel.identify(TIFFANY_ID);
        localStorage.setItem(TIFFANY_ID, anonymousDeviceId);
        return;
      }

      if (
        MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][
          anonymousDeviceId
        ] === DAPHNE_ID
      ) {
        mixpanel.alias(DAPHNE_ID);
        mixpanel.identify(DAPHNE_ID);
        localStorage.setItem(DAPHNE_ID, anonymousDeviceId);
        return;
      }

      mixpanel.alias(user.id);
      mixpanel.identify(user.id);
      localStorage.setItem(user.id, anonymousDeviceId);
    }
  }, [user?.id]);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/dashboard" element={<LayoutWrapper path="/dashboard" isOnDashboard />} />
      <Route
        path="/profile"
        element={
          <LayoutWrapper path="/profile">
            <ProfilePage />{' '}
          </LayoutWrapper>
        }
      />
      <Route
        path="/rates"
        element={
          <LayoutWrapper path="/rates">
            {' '}
            <RatesComparisonSummary />{' '}
          </LayoutWrapper>
        }
      ></Route>

      <Route
        path="/analytics"
        element={
          <LayoutWrapper path="/analytics">
            <RatesComparisonAnalytics />
          </LayoutWrapper>
        }
      />
      <Route path="/auto_rates" element={<AutoLoans />} />
      <Route path="/mortgages" element={<MortgageLoans />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot_password" element={<ForgetPassword />} />
      <Route path="/verify_password" element={<VerifyPassword />} />
      <Route path="/reset_password/:token" element={<ResetPassword />} />
      <Route path="/logout" element={<Landing logout={true} />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/partner" element={<Partner />} />
      <Route path="/partner/:partnerId" element={<FreeTrialReport />} />
      <Route path="/partner/:partnerId/free_trial" element={<FreeTrialReport />} />
      <Route path="/partner/:partnerId/free_trial/:freeTrialId" element={<FreeTrialReport />} />
      <Route path="/privacy_policy" element={<PrivacyPolicy />} />
      <Route path="/terms_and_conditions" element={<TermsAndCondition />} />
      <Route path="/terms_of_service" element={<TermsOfService />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/survey" element={<Surveys />} />
      <Route path="/admin-portal-survey-a7f5s" element={<SurveyAdminView />} />
      <Route path="/survey_UIExperience" element={<Experience />} />
      <Route path="/survey_finance_auto" element={<FinanceAuto />} />
      <Route path="/survey_insurance_home" element={<InsuranceHome />} />
      <Route path="/survey_insurance_auto" element={<InsuranceAuto />} />
      <Route path="/survey_finance_mortgage" element={<FinanceMortgage />} />
      <Route path="/survey_thankyou" element={<ThankYou />} />
      <Route path="/blogs" element={<Blog />} />
      <Route path="/blogs/:blogId" element={<BlogPost />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/:NotFound" element={<Landing />} />
    </Routes>
  );
};

export default App;
